/**
 * 통합 날짜/시간 포맷팅 Composable
 *
 * 프로젝트 전체에서 사용하는 날짜/시간 포맷팅 함수들을 통합
 */

import { Timestamp } from 'firebase/firestore'

// 다양한 타임스탬프 형태를 정의하는 유니온 타입
export type TimestampInput =
  | Date
  | Timestamp
  | { toDate(): Date }
  | { _seconds: number; _nanoseconds: number }
  | { seconds: number; nanoseconds?: number }
  | string
  | number
  | null
  | undefined

// 타입 가드 함수들
function isFirebaseTimestamp(obj: unknown): obj is Timestamp {
  return obj instanceof Timestamp
}

function hasToDateMethod(obj: unknown): obj is { toDate(): Date } {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    typeof (obj as { toDate?: unknown }).toDate === 'function'
  )
}

function hasSecondsNanoseconds(obj: unknown): obj is { _seconds: number; _nanoseconds: number } {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    typeof (obj as { _seconds?: unknown })._seconds === 'number' &&
    typeof (obj as { _nanoseconds?: unknown })._nanoseconds === 'number'
  )
}

function hasSeconds(obj: unknown): obj is { seconds: number } {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    typeof (obj as { seconds?: unknown }).seconds === 'number'
  )
}

/**
 * 다양한 형태의 타임스탬프를 Date 객체로 변환
 */
function toDate(input: TimestampInput): Date | null {
  if (!input) return null

  // 이미 Date 객체인 경우
  if (input instanceof Date) {
    return input
  }

  // Firebase Timestamp 객체
  if (isFirebaseTimestamp(input)) {
    return input.toDate()
  }

  // Firebase Timestamp toDate() 메소드가 있는 경우
  if (hasToDateMethod(input)) {
    return input.toDate()
  }

  // { _seconds, _nanoseconds } 형태
  if (hasSecondsNanoseconds(input)) {
    return new Timestamp(input._seconds, input._nanoseconds).toDate()
  }

  // seconds 속성이 있는 경우
  if (hasSeconds(input)) {
    return new Date(input.seconds * 1000)
  }

  // 문자열이나 숫자인 경우
  if (typeof input === 'string' || typeof input === 'number') {
    const date = new Date(input)
    if (!isNaN(date.getTime())) {
      return date
    }
  }

  return null
}

export function useUnifiedDate() {
  /**
   * 한국어 날짜 포맷 (24년 12월 30일)
   */
  const formatKoreanDate = (input: TimestampInput): string => {
    const date = toDate(input)
    if (!date) return ''

    const year = date.getFullYear().toString().slice(-2)
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')

    return `${year}년 ${month}월 ${day}일`
  }

  /**
   * 한국어 시간 포맷 (14시 30분)
   */
  const formatKoreanTime = (input: TimestampInput): string => {
    const date = toDate(input)
    if (!date) return ''

    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')

    return `${hours}시 ${minutes}분`
  }

  /**
   * 한국어 날짜+시간 포맷 (2024-12-30 14:30)
   */
  const formatDateTime = (input: TimestampInput): string => {
    const date = toDate(input)
    if (!date) return 'N/A'

    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  /**
   * 간단한 날짜 포맷 (12/30)
   */
  const formatSimpleDate = (input: TimestampInput): string => {
    const date = toDate(input)
    if (!date) return ''

    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')

    return `${month}/${day}`
  }

  /**
   * 간단한 시간 포맷 (14:30)
   */
  const formatSimpleTime = (input: TimestampInput): string => {
    const date = toDate(input)
    if (!date) return ''

    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')

    return `${hours}:${minutes}`
  }

  /**
   * 상대 시간 포맷 (방금 전, 5분 전, 1시간 전, 어제, 3일 전)
   */
  const formatRelativeTime = (input: TimestampInput, compact: boolean = false): string => {
    const date = toDate(input)
    if (!date) return compact ? '' : 'N/A'

    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffMinutes < 1) {
      return '방금 전'
    } else if (diffMinutes < 60) {
      return `${diffMinutes}분 전`
    } else if (diffHours < 24) {
      return `${diffHours}시간 전`
    } else if (diffDays === 1) {
      return '어제'
    } else if (diffDays < 7) {
      return `${diffDays}일 전`
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7)
      return `${weeks}주 전`
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30)
      return `${months}개월 전`
    } else {
      const years = Math.floor(diffDays / 365)
      return `${years}년 전`
    }
  }

  /**
   * API용 날짜 포맷 (20241230)
   */
  const formatDateForApi = (input: TimestampInput): number => {
    const date = toDate(input)
    if (!date) return 0

    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')

    return parseInt(`${year}${month}${day}`, 10)
  }

  /**
   * ISO 날짜 문자열 포맷 (2024-12-30)
   */
  const formatISO = (input: TimestampInput): string => {
    const date = toDate(input)
    if (!date) return ''

    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')

    return `${year}-${month}-${day}`
  }

  /**
   * KST 시간대 적용 날짜 포맷
   */
  const formatDateKST = (input: TimestampInput): string => {
    const date = toDate(input)
    if (!date) return ''

    // KST 시간대로 변환 (+9시간)
    const kstDate = new Date(date.getTime() + 9 * 60 * 60 * 1000)

    const year = kstDate.getUTCFullYear()
    const month = (kstDate.getUTCMonth() + 1).toString().padStart(2, '0')
    const day = kstDate.getUTCDate().toString().padStart(2, '0')
    const hours = kstDate.getUTCHours().toString().padStart(2, '0')
    const minutes = kstDate.getUTCMinutes().toString().padStart(2, '0')

    return `${year}-${month}-${day} ${hours}:${minutes}`
  }

  /**
   * 특정 일수 이내인지 확인
   */
  const isWithinDays = (input: TimestampInput, days: number): boolean => {
    const date = toDate(input)
    if (!date) return false

    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = diffMs / (1000 * 60 * 60 * 24)

    return diffDays >= 0 && diffDays <= days
  }

  /**
   * 오늘 날짜인지 확인
   */
  const isToday = (input: TimestampInput): boolean => {
    const date = toDate(input)
    if (!date) return false

    const today = new Date()
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    )
  }

  /**
   * 어제 날짜인지 확인
   */
  const isYesterday = (input: TimestampInput): boolean => {
    const date = toDate(input)
    if (!date) return false

    const yesterday = new Date()
    yesterday.setDate(yesterday.getDate() - 1)

    return (
      date.getFullYear() === yesterday.getFullYear() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getDate() === yesterday.getDate()
    )
  }

  /**
   * 날짜 차이 계산 (일 단위)
   */
  const getDaysDiff = (input1: TimestampInput, input2: TimestampInput): number => {
    const date1 = toDate(input1)
    const date2 = toDate(input2)

    if (!date1 || !date2) return 0

    const diffMs = Math.abs(date1.getTime() - date2.getTime())
    return Math.floor(diffMs / (1000 * 60 * 60 * 24))
  }

  return {
    // 포맷팅 함수들
    formatKoreanDate,
    formatKoreanTime,
    formatDateTime,
    formatSimpleDate,
    formatSimpleTime,
    formatRelativeTime,
    formatDateForApi,
    formatISO,
    formatDateKST,

    // 유틸리티 함수들
    isWithinDays,
    isToday,
    isYesterday,
    getDaysDiff,

    // 원본 변환 함수 (필요시 직접 사용)
    toDate,
  }
}
